import { API_BASE_URL } from '@/config/api'

interface PrintAndUploadReportOptions {
  iframeTitle: string
  reportName: string
  printTitle: string
  html?: string
  pdfBase64?: string
  onUploadError?: (message: string) => void
}

type UploadReportPdfResponse = {
  data?: {
    pdf_base64?: string | null
  }
}

function getToken(): string {
  return localStorage.getItem('token') ?? ''
}

function getFrameHtml(frame: HTMLIFrameElement | null, fallback = ''): string {
  const doc = frame?.contentDocument
  if (!doc) return fallback
  return `<!DOCTYPE html>${doc.documentElement.outerHTML}`
}

function escapeHtmlText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// Force HTML's <title> to `title` before Playwright renders it.
// Chromium embeds the page <title> as PDF /Title metadata, which the browser
// then uses as the suggested filename in the Save-as-PDF dialog. Setting
// `iframe.contentDocument.title` is unreliable for `srcDoc` iframes (the DOM
// update doesn't always serialize back into `documentElement.outerHTML`), so
// rewrite the source string directly to guarantee Playwright sees printTitle.
function injectHtmlTitle(html: string, title: string): string {
  if (!html) return html
  const escaped = escapeHtmlText(title)
  if (/<title\b[^>]*>[\s\S]*?<\/title>/i.test(html)) {
    return html.replace(/<title\b[^>]*>[\s\S]*?<\/title>/i, `<title>${escaped}</title>`)
  }
  if (/<\/head\s*>/i.test(html)) {
    return html.replace(/<\/head\s*>/i, `<title>${escaped}</title></head>`)
  }
  return html
}

async function uploadReportPdf(reportName: string, html?: string, pdfBase64?: string): Promise<string> {
  const token = getToken()
  const response = await fetch(`${API_BASE_URL}/print-reports/upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      report_name: reportName,
      html: pdfBase64 ? undefined : html,
      pdf_base64: pdfBase64 || undefined,
    }),
  })
  if (!response.ok) {
    let detail = `Upload print report failed (${response.status})`
    try {
      const body = (await response.json()) as { detail?: string; message?: string }
      detail = body.detail || body.message || detail
    } catch {
      // Keep the HTTP fallback if the server did not return JSON.
    }
    throw new Error(detail)
  }
  const body = (await response.json()) as UploadReportPdfResponse
  return body.data?.pdf_base64 || pdfBase64 || ''
}

function blurActiveElement(): void {
  // MUI Dialog set aria-hidden="true" trên #root khi modal open. Nếu Print
  // button vẫn giữ focus tại thời điểm browser print dialog mở → a11y warning
  // "Blocked aria-hidden on an element because its descendant retained focus".
  // Blur trước khi print() để focus quay về <body> (không bị aria-hidden).
  const active = document.activeElement as HTMLElement | null
  if (active && typeof active.blur === 'function') {
    active.blur()
  }
}

function printPdfBase64(pdfBase64: string, printTitle: string): void {
  const pdfBytes = Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0))
  const blob = new Blob([pdfBytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const frame = document.createElement('iframe')
  // Browser print dialog uses iframe.name (Chrome/Edge) hoặc document.title
  // làm filename mặc định khi Save as PDF. Set cả 2 để fallback đầy đủ.
  const titleNoExt = printTitle.replace(/\.pdf$/i, '')
  frame.name = titleNoExt
  frame.title = titleNoExt
  // Off-screen nhưng size THẬT (A4 ratio). Iframe 0×0 cho PDF blob khiến
  // Chrome PDF viewer extension không kịp init → `GET chrome-extension://invalid/
  // net::ERR_FAILED` khi print() được gọi.
  frame.style.position = 'fixed'
  frame.style.left = '-10000px'
  frame.style.top = '0'
  frame.style.width = '210mm'
  frame.style.height = '297mm'
  frame.style.border = '0'
  frame.style.opacity = '0'
  frame.style.pointerEvents = 'none'

  let cleaned = false
  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    URL.revokeObjectURL(url)
    frame.remove()
  }

  frame.addEventListener('load', () => {
    // Set title trên contentDocument của blob PDF iframe (same-origin nên ok)
    // → browser dùng làm filename trong Save as PDF dialog.
    try {
      if (frame.contentDocument) {
        frame.contentDocument.title = titleNoExt
      }
    } catch {
      // Cross-origin protection — fallback to document.title (already set bởi caller).
    }
    blurActiveElement()
    // Delay print() để Chrome PDF viewer extension (chrome-extension://...) kịp
    // init resource. Gọi quá sớm → extension fetch `chrome-extension://invalid/`
    // và ném ERR_FAILED ra console, đôi khi block luôn print dialog.
    window.setTimeout(() => {
      try {
        frame.contentWindow?.focus()
        frame.contentWindow?.addEventListener('afterprint', cleanup, { once: true })
        frame.contentWindow?.print()
      } catch {
        cleanup()
      }
      // Fallback cleanup (afterprint không fire trên một số browser khi user cancel).
      window.setTimeout(cleanup, 60_000)
    }, 300)
  })
  document.body.appendChild(frame)
  frame.src = url
}

export async function printAndUploadReport({
  iframeTitle,
  reportName,
  printTitle,
  html,
  pdfBase64,
  onUploadError,
}: PrintAndUploadReportOptions): Promise<void> {
  const frame = document.querySelector<HTMLIFrameElement>(`iframe[title="${iframeTitle}"]`)
  const originalTitle = document.title
  const titleNoExt = printTitle.replace(/\.pdf$/i, '')
  document.title = printTitle
  if (frame?.contentDocument) frame.contentDocument.title = titleNoExt

  let uploadedPdfBase64 = pdfBase64 || ''
  try {
    const sourceHtml = injectHtmlTitle(getFrameHtml(frame, html), titleNoExt)
    uploadedPdfBase64 = await uploadReportPdf(reportName, sourceHtml, pdfBase64)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Upload print report failed'
    onUploadError?.(message)
  }

  if (uploadedPdfBase64) {
    printPdfBase64(uploadedPdfBase64, printTitle)
  } else {
    blurActiveElement()
    frame?.contentWindow?.focus()
    frame?.contentWindow?.print()
  }
  window.setTimeout(() => {
    document.title = originalTitle
  }, 1000)
}
